import {
    Callout,
    Checkbox,
    DatePicker,
    DefaultEffects,
    Dialog,
    DialogType,
    Dropdown,
    DirectionalHint,
    getTheme,
    IDropdownOption,
    Icon,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';
import type { IOutOfOfficePreferredMethods } from 'shared-state/identity/atoms';

const MAX_ABSENCE_CHARS = 72;
type AbsencePresetKey =
    | 'meeting'
    | 'customer'
    | 'lunch'
    | 'holidays'
    | 'freshAir'
    | 'grocery'
    | 'multiDayEvent'
    | 'oooVacation'
    | 'oooSabbatical'
    | 'oooParentalLeave'
    | 'oooOutOfOffice';
const TODAY_ABSENCE_PRESET_KEYS: AbsencePresetKey[] = ['meeting', 'lunch', 'freshAir', 'grocery'];
const SET_DATE_ABSENCE_PRESET_KEYS: AbsencePresetKey[] = ['customer', 'multiDayEvent', 'oooVacation', 'oooSabbatical', 'oooParentalLeave', 'oooOutOfOffice'];

function formatDateToLocale(d: Date): string {
    const lang = localStorage.getItem('chosenLanguage') || 'de';
    return d.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function parseDateFromLocaleString(s: string): Date | null {
    if (!s || !s.trim()) return null;
    const parts = s.trim().split(/[./-]/);
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const d = new Date(year, month, day);
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
    return d;
}

function getToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function getTomorrow(): Date {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
}

function getCurrentTimeString(): string {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function parseTimeToMinutes(s: string): number {
    const [h = 0, m = 0] = s.split(':').map((x) => parseInt(x, 10) || 0);
    return Math.max(0, Math.min(23, h)) * 60 + Math.max(0, Math.min(59, m));
}

function addMinutesToTimeString(baseTime: string, minutesToAdd: number): string {
    const base = parseTimeToMinutes(baseTime);
    const total = (base + minutesToAdd) % (24 * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export interface IOutOfOfficeModalProps {
    isOpen: boolean;
    onConfirm: (payload: { returnDate: string; returnTime: string | null; absenceComment: string; preferredMethods: IOutOfOfficePreferredMethods }) => void;
    onDismiss: () => void;
}

const RETURN_TIME_PRESET_MINUTES = [30, 60, 120, 180, 240] as const;
const EXTENDED_RETURN_TIME_MINUTES = [30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390, 420, 450] as const;
const EXTENDED_PRESET_VALUES = new Set<number>(EXTENDED_RETURN_TIME_MINUTES);
const MAX_RETURN_DURATION_MINUTES = 450; // 7.5 hours

type DateQuickOption = 'today' | 'setDate';

function getReturnDurationLabel(minutes: number): string {
    if (minutes === 30) return IvicosStrings.focusTime30Min;
    if (minutes === 60) return IvicosStrings.focusTime1H;
    if (minutes === 90) return IvicosStrings.focusTime1_5H;
    if (minutes === 120) return IvicosStrings.focusTime2H;
    if (minutes === 150) return IvicosStrings.focusTime2_5H;
    if (minutes === 180) return IvicosStrings.focusTime3H;
    if (minutes < 60) return `${minutes} min`;
    if (minutes % 60 === 0) return `${minutes / 60} h`;
    return `${(minutes / 60).toFixed(1)} h`;
}

const OutOfOfficeModal: React.FC<IOutOfOfficeModalProps> = ({ isOpen, onConfirm, onDismiss }) => {
    const theme = getTheme();
    const [dateQuickOption, setDateQuickOption] = useState<DateQuickOption>('today');
    const [returnDate, setReturnDate] = useState<Date>(() => getToday());
    const [selectedTimePreset, setSelectedTimePreset] = useState<number | null>(30);
    const [totalMinutes, setTotalMinutes] = useState<number>(30);
    const [returnTime, setReturnTime] = useState<string | null>(() => addMinutesToTimeString(getCurrentTimeString(), 30));
    /** Separate draft per tab so switching Today ↔ Set date does not carry the comment across. */
    const [absenceCommentsByTab, setAbsenceCommentsByTab] = useState<Record<DateQuickOption, string>>({ today: '', setDate: '' });
    const absenceComment = absenceCommentsByTab[dateQuickOption];
    const [absenceDropdownOpen, setAbsenceDropdownOpen] = useState(false);
    const absenceInputRef = useRef<HTMLDivElement>(null);
    const [absenceError, setAbsenceError] = useState<string | null>(null);
    const [preferredMethods, setPreferredMethods] = useState<IOutOfOfficePreferredMethods>({
        email: true,
        chat: true,
        sms: false,
        call: false
    });

    const absencePresetOptions = useMemo(() => {
        const presetOptionsByKey: Record<AbsencePresetKey, { key: AbsencePresetKey; text: string }> = {
            meeting: { key: 'meeting', text: IvicosStrings.oooModalAbsenceOptionMeeting },
            customer: { key: 'customer', text: IvicosStrings.oooModalAbsenceOptionCustomer },
            lunch: { key: 'lunch', text: IvicosStrings.oooModalAbsenceOptionLunch },
            holidays: { key: 'holidays', text: IvicosStrings.oooModalAbsenceOptionHolidays },
            freshAir: { key: 'freshAir', text: IvicosStrings.oooModalAbsenceOptionFreshAir },
            grocery: { key: 'grocery', text: IvicosStrings.oooModalAbsenceOptionGrocery },
            multiDayEvent: { key: 'multiDayEvent', text: IvicosStrings.oooModalAbsenceOptionMultiDayEvent },
            oooVacation: { key: 'oooVacation', text: IvicosStrings.oooModalAbsenceOptionOooVacation },
            oooSabbatical: { key: 'oooSabbatical', text: IvicosStrings.oooModalAbsenceOptionOooSabbatical },
            oooParentalLeave: { key: 'oooParentalLeave', text: IvicosStrings.oooModalAbsenceOptionOooParentalLeave },
            oooOutOfOffice: { key: 'oooOutOfOffice', text: IvicosStrings.oooModalAbsenceOptionOooOutOfOffice }
        };

        const presetKeys = dateQuickOption === 'today' ? TODAY_ABSENCE_PRESET_KEYS : SET_DATE_ABSENCE_PRESET_KEYS;
        return presetKeys.map((key) => presetOptionsByKey[key]);
    }, [dateQuickOption]);
    const durationDropdownOptions: IDropdownOption[] = useMemo(
        () => EXTENDED_RETURN_TIME_MINUTES.map((min) => ({ key: min, text: getReturnDurationLabel(min) })),
        []
    );
    const minSetDate = useMemo(() => getTomorrow(), []);

    const applyReturnDuration = (durationMinutes: number) => {
        const clamped = Math.max(0, Math.min(MAX_RETURN_DURATION_MINUTES, durationMinutes));
        setTotalMinutes(clamped);
        setSelectedTimePreset(EXTENDED_PRESET_VALUES.has(clamped) ? clamped : null);
        setReturnTime(addMinutesToTimeString(getCurrentTimeString(), clamped));
    };

    const handleReturnDateTabChange = (item?: PivotItem) => {
        const nextTab = item?.props.itemKey as DateQuickOption | undefined;
        if (!nextTab || nextTab === dateQuickOption) return;

        setAbsenceDropdownOpen(false);
        setAbsenceError(null);

        if (nextTab === 'today') {
            setDateQuickOption('today');
            setReturnDate(getToday());

            if (returnTime == null) {
                applyReturnDuration(totalMinutes > 0 ? totalMinutes : 30);
            }
            return;
        }

        setDateQuickOption('setDate');
        setReturnDate(getTomorrow());
        setReturnTime(null);
    };

    useEffect(() => {
        if (isOpen) {
            setDateQuickOption('today');
            setReturnDate(getToday());
            setSelectedTimePreset(30);
            setTotalMinutes(30);
            setReturnTime(addMinutesToTimeString(getCurrentTimeString(), 30));
            setAbsenceError(null);
            setAbsenceDropdownOpen(false);
            setAbsenceCommentsByTab({ today: '', setDate: '' });
            setPreferredMethods({ email: true, chat: false, sms: false, call: false });
        }
    }, [isOpen]);

    const handleReturnDateChange = (d: Date | null | undefined) => {
        if (!d) return;
        const normalizedDate = new Date(d);
        normalizedDate.setHours(0, 0, 0, 0);

        if (normalizedDate < minSetDate) {
            setReturnDate(minSetDate);
            return;
        }

        setReturnDate(normalizedDate);
    };

    const handleCustomHoursChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        const n = Math.max(0, Math.min(23, parseInt(value || '0', 10) || 0));
        const customMin = totalMinutes % 60;
        applyReturnDuration(n * 60 + customMin);
    };

    const handleCustomMinutesChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        const n = Math.max(0, Math.min(59, parseInt(value || '0', 10) || 0));
        const customHours = Math.floor(totalMinutes / 60);
        applyReturnDuration(customHours * 60 + n);
    };

    const inputMatchesAnyPreset = (text: string): boolean => {
        const trimmed = text.trim().toLowerCase();
        if (!trimmed) return true;
        return absencePresetOptions.some((opt) => opt.text.toLowerCase() === trimmed || opt.text.toLowerCase().startsWith(trimmed));
    };

    const patchAbsenceCommentForActiveTab = (value: string) => {
        setAbsenceCommentsByTab((prev) => ({ ...prev, [dateQuickOption]: value }));
    };

    const handleAbsenceInputChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        const text = value ?? '';
        patchAbsenceCommentForActiveTab(text);
        setAbsenceError(null);
        if (!inputMatchesAnyPreset(text)) {
            setAbsenceDropdownOpen(false);
        }
    };

    const handleAbsencePresetSelect = (text: string) => {
        patchAbsenceCommentForActiveTab(text);
        setAbsenceError(null);
        setAbsenceDropdownOpen(false);
    };

    const validateAbsence = (): boolean => {
        const trimmed = absenceComment.trim();
        if (trimmed.length === 0) {
            setAbsenceError(IvicosStrings.oooModalAbsenceMinCharsError);
            return false;
        }
        if (!absencePresetOptions.some((opt) => opt.text === trimmed) && trimmed.length < 3) {
            setAbsenceError(IvicosStrings.oooModalAbsenceMinCharsError);
            return false;
        }
        setAbsenceError(null);
        return true;
    };

    const handleConfirm = () => {
        if (!validateAbsence()) return;
        if (isReturnTimeInPast) return;
        const trimmed = absenceComment.trim();
        if (trimmed.length > MAX_ABSENCE_CHARS) return;

        // Format date as YYYY-MM-DD to avoid timezone serialization issues
        const year = returnDate.getFullYear();
        const month = String(returnDate.getMonth() + 1).padStart(2, '0');
        const day = String(returnDate.getDate()).padStart(2, '0');
        const returnDateStr = `${year}-${month}-${day}`;

        onConfirm({
            returnDate: returnDateStr,
            returnTime,
            absenceComment: trimmed.slice(0, MAX_ABSENCE_CHARS),
            preferredMethods
        });
    };

    const charCount = absenceComment.length;
    const isOverMax = charCount > MAX_ABSENCE_CHARS;
    const trimmedComment = absenceComment.trim();
    const isPreset = absencePresetOptions.some((opt) => opt.text === trimmedComment);
    const isCustomTooShort = !isPreset && trimmedComment.length > 0 && trimmedComment.length < 3;

    const isReturnTimeInPast = useMemo(() => {
        if (!returnTime) return false;
        const now = new Date();
        const isToday = returnDate.getFullYear() === now.getFullYear() && returnDate.getMonth() === now.getMonth() && returnDate.getDate() === now.getDate();
        if (!isToday) return false;
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const returnMinutes = parseTimeToMinutes(returnTime);
        return returnMinutes < nowMinutes;
    }, [returnDate, returnTime]);
    const isConfirmDisabled = !trimmedComment || isOverMax || isCustomTooShort || isReturnTimeInPast;

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            minWidth="400px"
            maxWidth="600px"
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.oooModalTitle,
                closeButtonAriaLabel: IvicosStrings.oooModalCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)', maxHeight: 500, overflowY: 'auto' } }}
        >
            <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 568, overflow: 'hidden' } }}>
                {/* Return date */}
                {/* <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.oooModalReturnDate}
                </Text> */}
                <Pivot
                    selectedKey={dateQuickOption}
                    onLinkClick={handleReturnDateTabChange}
                    headersOnly={true}
                    styles={{
                        root: { display: 'flex' },
                        link: {
                            minWidth: 'auto',
                            height: 'auto',
                            lineHeight: '20px',
                            padding: '8px 12px',
                            marginRight: 8,
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.neutralLight}`,
                            backgroundColor: theme.palette.neutralLighter,
                            selectors: {
                                '&:before': { display: 'none' },
                                '&:hover': {
                                    borderColor: theme.palette.neutralLight,
                                    backgroundColor: theme.palette.neutralLighter
                                },
                                '& .ms-Pivot-text': {
                                    fontSize: 13,
                                    color: theme.palette.black
                                },
                                '&:hover .ms-Pivot-text': {
                                    color: theme.palette.black
                                }
                            }
                        },
                        linkIsSelected: {
                            borderColor: theme.palette.themePrimary,
                            backgroundColor: theme.palette.themePrimary,
                            selectors: {
                                '&:before': { display: 'none' },
                                '&:hover': {
                                    borderColor: theme.palette.themePrimary,
                                    backgroundColor: theme.palette.themePrimary
                                },
                                '& .ms-Pivot-text': {
                                    color: theme.palette.white
                                },
                                '&:hover .ms-Pivot-text': {
                                    color: theme.palette.white
                                }
                            }
                        }
                    }}
                >
                    <PivotItem itemKey="today" headerText={IvicosStrings.oooModalReturnDateToday} />
                    <PivotItem itemKey="setDate" headerText={IvicosStrings.oooModalReturnDateSetDate} />
                </Pivot>
                {dateQuickOption === 'setDate' && (
                    <DatePicker
                        value={returnDate}
                        onSelectDate={handleReturnDateChange}
                        minDate={minSetDate}
                        formatDate={(d) => (d ? formatDateToLocale(d) : '')}
                        parseDateFromString={parseDateFromLocaleString}
                    />
                )}

                {dateQuickOption === 'today' && (
                    <>
                        {/* Return time */}
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                            {IvicosStrings.oooModalReturnTime}
                        </Text>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
                                {RETURN_TIME_PRESET_MINUTES.map((min) => (
                                    <button
                                        key={min}
                                        type="button"
                                        onClick={() => applyReturnDuration(min)}
                                        style={{
                                            padding: '8px 12px',
                                            borderRadius: 4,
                                            border: `1px solid ${selectedTimePreset === min ? theme.palette.themePrimary : theme.palette.neutralLight}`,
                                            backgroundColor: selectedTimePreset === min ? theme.palette.themePrimary : theme.palette.neutralLighter,
                                            color: selectedTimePreset === min ? theme.palette.white : theme.palette.black,
                                            cursor: 'pointer',
                                            fontSize: 13
                                        }}
                                    >
                                        {getReturnDurationLabel(min)}
                                    </button>
                                ))}
                            </Stack>
                            <Dropdown
                                label={IvicosStrings.focusTimeMore}
                                placeholder={IvicosStrings.focusTimeMore}
                                options={durationDropdownOptions}
                                selectedKey={selectedTimePreset != null && EXTENDED_PRESET_VALUES.has(selectedTimePreset) ? selectedTimePreset : undefined}
                                onChange={(_, opt) => opt && applyReturnDuration(Number(opt.key))}
                                styles={{
                                    root: { maxWidth: 150 },
                                    label: { fontSize: 11, fontWeight: 400 },
                                    dropdown: { borderRadius: 4 },
                                    dropdownItemsWrapper: {
                                        maxHeight: 180,
                                        overflowY: 'auto'
                                    }
                                }}
                            />
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                                    <TextField
                                        type="number"
                                        value={String(Math.floor(totalMinutes / 60))}
                                        onChange={handleCustomHoursChange}
                                        styles={{ root: { width: 56 }, fieldGroup: { borderRadius: 4 } }}
                                    />
                                    <Text variant="small">h</Text>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                                    <TextField
                                        type="number"
                                        value={String(totalMinutes % 60)}
                                        onChange={handleCustomMinutesChange}
                                        styles={{ root: { width: 56 }, fieldGroup: { borderRadius: 4 } }}
                                    />
                                    <Text variant="small">min</Text>
                                </Stack>
                            </Stack>
                            <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                                {IvicosStrings.focusTimeTotalDuration}: {totalMinutes} min
                            </Text>
                            {isReturnTimeInPast && (
                                <Text variant="small" style={{ color: theme.palette.red }}>
                                    {IvicosStrings.oooModalReturnTimePastError}
                                </Text>
                            )}
                        </Stack>
                    </>
                )}

                {/* Absence comment */}
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.oooModalAbsenceComment}
                </Text>
                <Stack tokens={{ childrenGap: 4 }}>
                    <div ref={absenceInputRef} style={{ position: 'relative', maxWidth: 400 }}>
                        <TextField
                            value={absenceComment}
                            onChange={handleAbsenceInputChange}
                            onFocus={() => setAbsenceDropdownOpen(true)}
                            onBlur={validateAbsence}
                            maxLength={MAX_ABSENCE_CHARS}
                            styles={{ root: { width: '100%' }, fieldGroup: { borderRadius: 4 } }}
                            onRenderSuffix={() => (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Open preset options"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAbsenceDropdownOpen((o) => !o);
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && setAbsenceDropdownOpen((o) => !o)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: 4,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Icon iconName="ChevronDown" style={{ fontSize: 12, color: theme.palette.neutralSecondary }} />
                                </span>
                            )}
                        />
                        {absenceDropdownOpen && absenceInputRef.current && (
                            <Callout
                                target={absenceInputRef.current}
                                onDismiss={() => setAbsenceDropdownOpen(false)}
                                directionalHint={DirectionalHint.bottomLeftEdge}
                                isBeakVisible={false}
                                style={{
                                    padding: 4,
                                    borderRadius: 4,
                                    boxShadow: DefaultEffects.elevation8,
                                    background: theme.palette.white,
                                    minWidth: 280
                                }}
                            >
                                <Stack tokens={{ childrenGap: 2 }}>
                                    {absencePresetOptions.map((opt) => (
                                        <div
                                            key={opt.key}
                                            onClick={() => handleAbsencePresetSelect(opt.text)}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                backgroundColor: theme.palette.neutralLighter
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = theme.palette.neutralLight;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = theme.palette.neutralLighter;
                                            }}
                                        >
                                            <Text variant="small">{opt.text}</Text>
                                        </div>
                                    ))}
                                </Stack>
                            </Callout>
                        )}
                    </div>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginTop: 4, maxWidth: 400 }}>
                        <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                            {IvicosStrings.oooModalAbsenceHint}
                        </Text>
                        <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                            {charCount}/{MAX_ABSENCE_CHARS}
                        </Text>
                    </Stack>
                    {absenceError && (
                        <Text variant="small" style={{ color: theme.palette.red }}>
                            {absenceError}
                        </Text>
                    )}
                    {isOverMax && (
                        <Text variant="small" style={{ color: theme.palette.red }}>
                            Maximum {MAX_ABSENCE_CHARS} characters.
                        </Text>
                    )}
                </Stack>

                {/* Preferred message receipt methods */}
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.oooModalPreferredMethods}
                </Text>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Checkbox
                        label={IvicosStrings.oooModalPreferredEmail}
                        checked={preferredMethods.email}
                        onChange={(_, c) => setPreferredMethods((m) => ({ ...m, email: !!c }))}
                    />
                    <Checkbox
                        label={IvicosStrings.oooModalPreferredChat}
                        checked={preferredMethods.chat}
                        onChange={(_, c) => setPreferredMethods((m) => ({ ...m, chat: !!c }))}
                    />
                </Stack>

                {/* Actions */}
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        onClick={onDismiss}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.neutralLight}`,
                            backgroundColor: 'transparent',
                            color: theme.palette.black,
                            cursor: 'pointer',
                            fontSize: 14
                        }}
                    >
                        {IvicosStrings.oooModalCancel}
                    </button>
                    <PrimaryButton text={IvicosStrings.oooModalConfirm} onClick={handleConfirm} disabled={isConfirmDisabled} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default OutOfOfficeModal;
